import { Component, ErrorInfo, ReactNode } from 'react';
import { ErrorPage } from '../features/error/pages/ErrorPage.tsx';
import { BaseLayout, isChunkLoadError, reloadOnceForStaleChunk } from '@insio/ui-toolkit';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  reloading: boolean;
}

/**
 * Error Boundary component that catches JavaScript errors anywhere in the child component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 *
 * According to constitution: "Introduce AppErrorBoundary sibling to AppRouter for global error handling"
 */
class AppErrorBoundaryClass extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, reloading: false };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    // Update state so the next render will show the fallback UI (or nothing while reloading)
    return { hasError: true, error, errorInfo: null, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Stale chunk after a deploy — reload once instead of showing the error UI
    if (isChunkLoadError(error) && reloadOnceForStaleChunk()) {
      return;
    }

    // Log error details for debugging
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught an error:', error, errorInfo);

    this.setState({
      error,
      errorInfo,
      reloading: false
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.reloading) {
      return null;
    }
    if (this.state.hasError) {
      return (
        <BaseLayout>
          <BaseLayout.Content className='flex items-center justify-center min-h-screen bg-background-base'>
            <ErrorPage
              mode='boundary'
              error={this.state.error}
              errorInfo={
                this.state.errorInfo
                  ? { componentStack: this.state.errorInfo.componentStack || '' }
                  : null
              }
              onReload={this.handleReload}
              onGoHome={this.handleGoHome}
            />
          </BaseLayout.Content>
        </BaseLayout>
      );
    }
    return this.props.children;
  }
}

/**
 * Error fallback UI component with localized messages
 */
/**
 * Export a wrapper component that provides the error boundary
 */
export function AppErrorBoundary({ children }: Props) {
  return <AppErrorBoundaryClass>{children}</AppErrorBoundaryClass>;
}

export default AppErrorBoundary;
